import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { MarkdownView } from '../../fields/markdown/MarkdownView';
import type { IPanelWidthOptions } from '../../form/panels/IPanelWidthOptions';
import { usePanelWidth } from '../../form/panels/usePanelWidth';
import { ArticleAside } from './ArticleAside';
import type { IArticleTabProps } from './IArticleTabProps';

/** Бічна частина статті (прототип v4 kbAsideW): 280px типово, 200–560px. */
const ASIDE: IPanelWidthOptions = { key: 'po.articleAsideWidth', initial: 280, min: 200, max: 560 };
const votedKey = (id: string) => `po.kbVoted.${id}`;
const wasVoted = (id: string) => { try { return localStorage.getItem(votedKey(id)) === '1'; } catch { return false; } };

/**
 * Вкладка «Читання» статті (UIUX forms/tabs — Knowledge Base: Read · Edit; прототип v4): картка з категорією, текстом Markdown і «Ця стаття
 * була корисною? Так · N / Ні · N» (голос зберігається в записі, повторно не голосується; без дозволу коментувати — кнопки вимкнені, лише
 * лічильники); роздільник 8px (тягнути — ширина, подвійний клік — типова) і праворуч — зміст, власник, перегляд, пов'язані статті.
 */
export function ArticleReadTab(props: IArticleTabProps) {
  const { t } = useTranslation();
  const strings = useModuleStrings();
  const { ws, rt, record, editor } = props;
  const [voted, setVoted] = useState(() => wasVoted(record.id));
  const helpful = (record.ext?.['helpful'] ?? {}) as { yes?: number; no?: number };
  const category = String(record.values['category'] ?? '');
  const canVote = record.permissions?.comment !== false;
  const aside = usePanelWidth(ASIDE);
  const vote = (k: 'yes' | 'no') => {
    setVoted(true);
    try { localStorage.setItem(votedKey(record.id), '1'); } catch { /* сховище недоступне — голос діє до перезавантаження */ }
    void editor.patchNow({}, undefined, { helpful: { yes: helpful.yes ?? 0, no: helpful.no ?? 0, [k]: (helpful[k] ?? 0) + 1 } });
  };
  return (
    <div className="rtab-art">
      <article className="rtab-art__main">
        <div className="rtab-art__col">
          {category && <span className="rtab-art__cat">{strings.option(rt.key, 'category', category, category)}</span>}
          <MarkdownView text={String(record.values['body'] ?? '')} ws={ws} emptyText={t('recordPage.article.empty')} />
          <div className="rtab-art__vote">
            <span className="rtab-art__q">{t('recordPage.article.helpful')}</span>
            {voted
              ? <span className="rtab-art__thanks">{t('recordPage.article.thanks', { count: helpful.yes ?? 0 })}</span>
              : <>
                <button type="button" className="rtab-chipbtn" disabled={!canVote} onClick={() => vote('yes')}>{t('recordPage.article.yes', { count: helpful.yes ?? 0 })}</button>
                <button type="button" className="rtab-chipbtn" disabled={!canVote} onClick={() => vote('no')}>{t('recordPage.article.no', { count: helpful.no ?? 0 })}</button>
              </>}
          </div>
        </div>
      </article>
      <div className="rec-split rtab-art__split" role="separator" aria-orientation="vertical" aria-label={t('recordPage.article.resize')} title={t('recordPage.article.resize')}
        onMouseDown={aside.start} onDoubleClick={aside.reset}><span /></div>
      <ArticleAside {...props} width={aside.width} />
    </div>
  );
}
